import { type Home, type User, UserRole } from '@lmc/ts-common'
import { Modal } from 'antd'
import { showToast } from 'common/utils'
import useJobTitles, { JOB_TITLES_QUERY_KEY } from 'hooks/useUser/useJobTitles'
import useUser from 'hooks/useUser/useUser'
import { NAMESPACE } from 'locale'
import { reportsConstants } from 'locale/team'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { buildCreatePayload } from './buildCreatePayload'
import { getDefaultCapabilities } from './capabilityToggles'
import { Step1Details } from './Step1Details'
import { Step2Role } from './Step2Role'
import { Step3Capabilities } from './Step3Capabilities'
import type { WizardData, WizardRole, WizardScope } from './types'

export type AddTeamMemberWizardProps = {
  open: boolean
  onClose: () => void
  jobTitleSuggestions?: string[]
  // CM-3388: scope decides the role roster, the Step 1 Site field, and the
  // create payload's home/homeGroup. Defaults to 'single-site' so TeamListV2's
  // usage is behaviour-identical.
  scope?: WizardScope
  // CM-3388: homes for the Step 1 Site multi-select (specific-sites scope).
  // The group screen passes its already-resolved list (redux with a
  // useHomesList fallback) so the options match the rows; when omitted,
  // Step1Details falls back to the redux reference list.
  homes?: Home[]
  testId?: string
}

type Step = 1 | 2 | 3

const initialDataFor = (role: WizardRole): WizardData => ({
  firstName: '',
  lastName: '',
  email: '',
  jobTitle: '',
  role,
  capabilities: getDefaultCapabilities(role),
  homes: []
})

// Matches the legacy AddTeamMemberModal default: restricted viewers
// (Senior Carer, Management) can't invite Admins, so don't pre-select
// Admin for them — otherwise they could land on step 2 with Admin
// already chosen and reach step 3 / Send invite without ever touching
// the (correctly disabled) Admin option.
const computeInitialRole = (
  viewerRole: UserRole | undefined,
  scope: WizardScope
): WizardRole => {
  // Group-dashboard scope offers only group roles; default to Group Manager to
  // match the legacy AddTeamMemberModal's `groupDashboardUserRoles` initial.
  if (scope === 'group-dashboard') {
    return UserRole.groupManager
  }
  if (
    viewerRole === UserRole.seniorCarer ||
    viewerRole === UserRole.management
  ) {
    return UserRole.carer
  }
  return UserRole.careHomeAdmin
}

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
  line-height: 32px;
  font-weight: 500;
  color: ${({ theme }) => theme.color.neutral.color3};
  text-transform: none;
`

export const AddTeamMemberWizard = ({
  open,
  onClose,
  jobTitleSuggestions,
  scope = 'single-site',
  homes,
  testId
}: AddTeamMemberWizardProps) => {
  const { t } = useTranslation(NAMESPACE.TEAM)
  const queryClient = useQueryClient()
  const { jobTitles } = useJobTitles({ enabled: open })
  // A non-empty explicit prop wins (tests/Storybook inject a fixed list);
  // otherwise use the fetched suggestions. Note the length check: callers may
  // pass an empty array, and `?? ` wouldn't fall through on `[]`. Gated on
  // `open` so a closed, still-mounted wizard doesn't fetch.
  const resolvedJobTitleSuggestions =
    jobTitleSuggestions && jobTitleSuggestions.length > 0
      ? jobTitleSuggestions
      : jobTitles
  const viewer = useSelector(
    (s: { global: { user?: { role?: UserRole; homeGroup?: string } } }) =>
      s.global.user
  )
  const viewerRole = viewer?.role
  // TODO(CM-3388): homeGroup isn't typed on the redux user yet; read defensively.
  const viewerHomeGroup = viewer?.homeGroup
  const initialRole = useMemo(
    () => computeInitialRole(viewerRole, scope),
    [viewerRole, scope]
  )

  const [step, setStep] = useState<Step>(1)
  const [data, setData] = useState<WizardData>(() =>
    initialDataFor(initialRole)
  )

  const reset = useCallback(() => {
    setStep(1)
    setData(initialDataFor(initialRole))
  }, [initialRole])

  const handleClose = useCallback(() => {
    reset()
    onClose()
  }, [onClose, reset])

  // Re-seed on each open transition. The wizard stays mounted while closed, so
  // when the group screen reopens it in a different scope (e.g. "Add to specific
  // sites" → "Add to Group Dashboard") the role/capabilities/homes must reset to
  // the new scope's defaults — otherwise step 2's roster, the step flow, and the
  // create payload would carry the previous scope's stale state.
  const wasOpen = useRef(open)
  useEffect(() => {
    if (open && !wasOpen.current) {
      setStep(1)
      setData(initialDataFor(initialRole))
    }
    wasOpen.current = open
  }, [open, initialRole])

  const { createUser, isLoadingCreate } = useUser({
    onCreateSuccess: () => {
      // The new member may have introduced a job title that isn't in the
      // home's list yet; invalidate so the next wizard open refetches it
      // (otherwise the 5-min React Query staleTime hides it until a refresh).
      queryClient.invalidateQueries([JOB_TITLES_QUERY_KEY])
      showToast({
        title: t(reportsConstants.WIZARD_INVITE_SENT_TOAST),
        type: 'success'
      })
      handleClose()
    }
  })

  const handleChange = (next: WizardData) => {
    if (next.role !== data.role) {
      setData({ ...next, capabilities: getDefaultCapabilities(next.role) })
    } else {
      setData(next)
    }
  }

  const handleSendInvite = () => {
    // For Carer.name shape, see CreatePayload doc in buildCreatePayload.ts.
    createUser(
      buildCreatePayload(data, { scope, viewerHomeGroup }) as Partial<User>
    )
  }

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      width={647}
      destroyOnClose
      title={<Title>{t(reportsConstants.WIZARD_TITLE)}</Title>}
      data-testid={testId}
    >
      {step === 1 && (
        <Step1Details
          value={data}
          onChange={handleChange}
          onNext={() => setStep(2)}
          onCancel={handleClose}
          jobTitleSuggestions={resolvedJobTitleSuggestions}
          scope={scope}
          homes={homes}
          testId={testId ? `${testId}-step1` : undefined}
        />
      )}
      {step === 2 && (
        <Step2Role
          value={data}
          onChange={handleChange}
          onBack={() => setStep(1)}
          onNext={() => setStep(3)}
          onSendInvite={handleSendInvite}
          isSubmitting={isLoadingCreate}
          scope={scope}
          testId={testId ? `${testId}-step2` : undefined}
        />
      )}
      {step === 3 && (
        <Step3Capabilities
          value={data}
          onChange={handleChange}
          onBack={() => setStep(2)}
          onSendInvite={handleSendInvite}
          isSubmitting={isLoadingCreate}
          testId={testId ? `${testId}-step3` : undefined}
        />
      )}
    </Modal>
  )
}
